import {
    Badge,
    Button,
    Card,
    CardHeader,
    Chip,
    EmptyState,
    ErrorState,
    LoadingRows,
    PageHeader,
} from '@shared/ui';
import { useState } from 'react';
import { Link } from 'react-router';

import { useClientTarget } from '../hooks/use-client-target';
import { useClients } from '../hooks/use-clients';
import { describeTarget, targetParams } from '../schemas/target';

import {
    applyClientFilters,
    CLIENT_FILTERS,
    countNeedingOwner,
    FILTER_HINT,
    FILTER_LABEL,
    type ClientFilter,
} from './client-filter';
import { EnvironmentScope } from './EnvironmentScope';

import type { ClientSummary } from '../schemas/client';

/**
 * The clients in one environment.
 *
 * ## What is NOT a column here
 *
 * The owner. The catalog knows about clients; people live in identity-api's
 * access store, and there is no read that lists them per client. A column that
 * is empty on every row is worse than no column, so the owner appears on the
 * detail screen — where a single client can be asked about — and this list
 * shows only what the catalog can actually answer.
 *
 * `status` is shown for the same reason it exists upstream, with a caveat worth
 * knowing: a client whose owner failed to provision is `active` here like any
 * other. The catalog cannot tell the two apart.
 */
export function ClientsPage() {
    const targetState = useClientTarget();
    const { target, isSlotMissing } = targetState;
    const clients = useClients(target);

    // `ready` by default: the point of the default is to get known-broken rows
    // out of the way, not to claim the rest are finished. See `client-filter`.
    const [filter, setFilter] = useState<ClientFilter>('ready');
    const [search, setSearch] = useState('');

    const all = clients.data ?? [];
    const shown = applyClientFilters(all, filter, search);
    const needingOwner = countNeedingOwner(all);

    return (
        <>
            <PageHeader
                eyebrow="Tenants"
                title="Clients"
                description="Every client with access to the platform. The ones that arrive through Salesforce register themselves; the rest are created here."
                actions={
                    <Link
                        to={{
                            pathname: '/clients/new',
                            search: new URLSearchParams(targetParams(target)).toString(),
                        }}
                    >
                        <Button>New client</Button>
                    </Link>
                }
            />

            <EnvironmentScope state={targetState} />

            <div className="mb-4 flex flex-wrap items-center gap-2.5">
                {CLIENT_FILTERS.map((option) => (
                    <Chip
                        key={option}
                        active={filter === option}
                        onClick={() => {
                            setFilter(option);
                        }}
                    >
                        {FILTER_LABEL[option]}
                        {/* The count rides on the filter that would show them,
                            so the number is next to the thing that acts on it. */}
                        {option === 'needs-owner' && needingOwner > 0 && (
                            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">
                                {needingOwner}
                            </span>
                        )}
                    </Chip>
                ))}

                <input
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                    }}
                    placeholder="Search by name or address"
                    aria-label="Search clients"
                    className="ml-auto w-64 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[13px] outline-none transition-colors placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand-50"
                />
            </div>

            <Card>
                <CardHeader
                    title="Clients"
                    subtitle={clients.data ? countLabel(shown.length) : undefined}
                    action={
                        <span className="text-[12.5px] text-gray-500">{FILTER_HINT[filter]}</span>
                    }
                />
                <ClientsTable
                    state={targetState}
                    query={clients}
                    rows={shown}
                    slotMissing={isSlotMissing}
                    narrowed={shown.length !== all.length}
                />
            </Card>
        </>
    );
}

const countLabel = (count: number) => (count === 1 ? '1 client' : `${String(count)} clients`);

function ClientsTable({
    state,
    query,
    rows,
    slotMissing,
    narrowed,
}: {
    state: ReturnType<typeof useClientTarget>;
    query: ReturnType<typeof useClients>;
    rows: ClientSummary[];
    slotMissing: boolean;
    narrowed: boolean;
}) {
    // Three different answers, and they lead to three different actions:
    // choose a target, create the first client, or fix what broke.
    if (slotMissing) {
        return (
            <EmptyState
                title="No slot chosen"
                description="Type a slot above to point at one of the ephemeral deployments."
            />
        );
    }

    if (query.isPending) return <LoadingRows label="Loading clients…" />;
    if (query.isError) {
        return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
    }

    if (query.data.length === 0) {
        return (
            <EmptyState
                title={`No clients in ${describeTarget(state.target)}`}
                description="Nothing has been created in this environment yet. Check the environment above if you expected to see something."
            />
        );
    }

    // Four empty states, not one. "This environment has none" and "your filter
    // hid them all" send the operator to different controls, and saying the
    // wrong one sends them looking in the wrong place.
    if (rows.length === 0) {
        return (
            <EmptyState
                title="Nothing matches"
                description={
                    narrowed
                        ? 'Every client in this environment is filtered out. Try All, or clear the search.'
                        : 'No client matches that search.'
                }
            />
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
                <thead>
                    <tr>
                        <Th>Client</Th>
                        <Th>Address</Th>
                        <Th>Status</Th>
                        <Th>Owner</Th>
                        <Th>Created</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((client) => (
                        <Row key={client.tenantId} client={client} state={state} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Th({ children }: { children: React.ReactNode }) {
    return (
        <th className="whitespace-nowrap border-b border-gray-100 bg-gray-50 px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {children}
        </th>
    );
}

function Row({
    client,
    state,
}: {
    client: ClientSummary;
    state: ReturnType<typeof useClientTarget>;
}) {
    const search = new URLSearchParams(targetParams(state.target)).toString();

    return (
        <tr className="hover:bg-gray-50">
            <td className="border-b border-gray-100 px-5 py-3">
                <Link
                    to={{ pathname: `/clients/${client.tenantId}`, search }}
                    className="font-semibold text-gray-900 hover:text-brand-700 hover:underline"
                >
                    {client.displayName}
                </Link>
            </td>
            <td className="border-b border-gray-100 px-5 py-3">
                {/* Absent is not empty: a client created before the slug existed
                    has none, and saying so beats a blank cell. */}
                {client.slug ? (
                    <span className="font-mono text-[12.5px] text-gray-700">{client.slug}</span>
                ) : (
                    <span className="text-[12.5px] text-gray-400">None</span>
                )}
            </td>
            <td className="border-b border-gray-100 px-5 py-3">
                {client.isSandbox === true ? (
                    <Badge tone="warning" dot>
                        Sandbox
                    </Badge>
                ) : (
                    <Badge tone="active" dot>
                        {client.status}
                    </Badge>
                )}
            </td>
            <td className="border-b border-gray-100 px-5 py-3">
                <OnboardingCell client={client} search={search} />
            </td>
            <td className="border-b border-gray-100 px-5 py-3 text-[12.5px] text-gray-500">
                {formatDate(client.createdAt)}
            </td>
        </tr>
    );
}

/**
 * Three outcomes, and the third is the one that matters most.
 *
 * An absent answer gets a muted dash, not a tick and not a warning: the
 * directory could not be asked, so the honest report is that we do not know.
 * Either substitute would be a claim, and the wrong one costs an operator a
 * trip to fix something that works.
 */
function OnboardingCell({ client, search }: { client: ClientSummary; search: string }) {
    if (client.hasOwner === false) {
        return (
            <Link to={{ pathname: `/clients/${client.tenantId}/owner`, search }}>
                <Badge tone="warning" dot>
                    Needs owner
                </Badge>
            </Link>
        );
    }

    if (client.hasOwner === true) {
        return (
            <Badge tone="success" dot>
                Has owner
            </Badge>
        );
    }

    return (
        <span
            className="text-[12.5px] text-gray-400"
            title="The directory could not be reached — this is not a claim about the client"
        >
            —
        </span>
    );
}

function formatDate(iso: string): string {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime())
        ? '—'
        : parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
